// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

/* eslint-disable jsx-a11y/anchor-is-valid */

import React, {MouseEvent, SyntheticEvent} from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { bindActionCreators } from "redux";
import { strings, interpolate } from "src/common/strings";
import {getPrimaryGreenTheme, getPrimaryRedTheme} from "src/common/themes";
import IProjectActions, * as projectActions from "src/redux/actions/projectActions";
import IApplicationActions, * as applicationActions from "src/redux/actions/applicationActions";
import IAppTitleActions, * as appTitleActions from "src/redux/actions/appTitleActions";
import { CloudFilePicker } from "../../common/cloudFilePicker/cloudFilePicker";
import Confirm from "../../common/confirm/confirm";
import "./homePage.scss";
import { constants } from "src/common/constants";
import {
    IApplicationState, IConnection, IProject,
    ErrorCode, AppError, IAppSettings, IAuth, IImpersonation
} from "src/models/applicationState";
import { fillTagsColor } from "src/common/utils";
import { toast } from "react-toastify";
import { HomeProjectView } from "./homeProjectView";
import { RinktMLClient } from "src/services/rinktService";
import Alert from "../../common/alert/alert";
import {PrimaryButton, Spinner, SpinnerSize} from "@fluentui/react";
import IImpersonationActions, * as impersonationActions from "src/redux/actions/impersonationActions";
import RecentProjects from "./recentProjects";
import Users from "./users";

export interface IHomePageProps extends RouteComponentProps, React.Props<HomePage> {
    recentProjects: IProject[];
    connections: IConnection[];
    projectActions: IProjectActions;
    applicationActions: IApplicationActions;
    impersonationActions: IImpersonationActions;
    appSettings: IAppSettings;
    project: IProject;
    appTitleActions: IAppTitleActions;
    auth: IAuth;
    impersonation: IImpersonation;
}

export interface IHomePageState {
    cloudPickerOpen: boolean;
    projectsLoading: boolean;
    projectLoading: boolean;
    searchKeyword: string;
    isError?: boolean;
    errorTitle?: string;
    errorMessage?: string;
}

function mapStateToProps(state: IApplicationState) {
    return {
        recentProjects: state.recentProjects,
        connections: state.connections,
        appSettings: state.appSettings,
        project: state.currentProject,
        auth: state.auth,
        impersonation: state.impersonation,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        projectActions: bindActionCreators(projectActions, dispatch),
        impersonationActions: bindActionCreators(impersonationActions, dispatch),
        applicationActions: bindActionCreators(applicationActions, dispatch),
        appTitleActions: bindActionCreators(appTitleActions, dispatch),
    };
}

@connect(mapStateToProps, mapDispatchToProps)
export default class HomePage extends React.Component<IHomePageProps, IHomePageState> {

    public state: IHomePageState = {
        cloudPickerOpen: false,
        projectsLoading: false,
        projectLoading: false,
        searchKeyword: ''
    };
    private homeProjectViewRef: React.RefObject<HomeProjectView> = React.createRef();
    private deleteConfirmRef = React.createRef<Confirm>();
    private cloudFilePickerRef = React.createRef<CloudFilePicker>();
    private importConfirmRef: React.RefObject<Confirm> = React.createRef();

    public async componentDidMount() {
        // if (!this.props.recentProjects || this.props.recentProjects.length === 0) {
        this.props.appTitleActions.setTitle("Welcome");
        document.title = strings.homePage.title + " - " + strings.appName;
        await this.updateProjectsAndImpersonators();
        this.setupViewportHeight();
        // }
    }

    public componentDidUpdate(prevProps: Readonly<IHomePageProps>) {
        if (prevProps.impersonation.impersonatedUser?.uid !== this.props.impersonation.impersonatedUser?.uid) {
            this.updateProjectsAndImpersonators();
        }
    }

    public setupViewportHeight() {
        const body: HTMLBodyElement = document.querySelector('body');
        const listsContainer: HTMLDivElement = document.querySelector('.app-homepage-lists-container');
        const listItems = document.querySelectorAll('.condensed-list-items');

        if (listsContainer && listItems) {
            const handleResize = () => {
                (listItems[0] as HTMLElement).style.maxHeight = `${body.offsetHeight - 300}px`;
                if (listItems.length > 1){
                    (listItems[1] as HTMLElement).style.maxHeight = `${body.offsetHeight - 300}px`;
                }

                if (body.offsetHeight < 520) {
                    listsContainer.style.minHeight = '0px';
                } else {
                    listsContainer.style.minHeight = '300px';
                }
            }

            handleResize();

            // @ts-ignore
            new ResizeObserver(handleResize).observe(body);
        } else {
            console.warn('Cannot setup ResizeObserver - DOM elements not mounted')
        }
    }

    public async updateProjectsAndImpersonators() {
        this.setState({ projectsLoading: true });
        const rinktClient = new RinktMLClient(this.props.auth.idToken, this.props.impersonation);

        try {
            const initialState = await (await rinktClient.getProjects()).data;
            await this.props.projectActions.setRecentProjects(initialState.recentProjects);
            await this.props.impersonationActions.setImpersonators(initialState.readAdmins);
            this.setState({ projectsLoading: false });
        } catch (err) {
            this.setState({
                projectsLoading: false,
                isError: true,
                errorTitle: "Failed to load projects.",
                errorMessage: err.message,
            });
        }
    }

    public render() {
        return (
            <div className="app-homepage" id="pageHome">
                <div className="app-homepage-main">
                    <div className="app-homepage-main-actions">
                        <PrimaryButton
                            onClick={this.createNewProject}
                            theme={getPrimaryGreenTheme()}
                            type="submit"
                        >
                            Create new project
                        </PrimaryButton>
                        <input
                            type='text'
                            placeholder='Search projects'
                            className='searchbar'
                            onChange={(event) => this.setState({ searchKeyword: event.target.value.trim()})}
                        />
                    </div>
                    <div className="app-homepage-lists-container">
                        <RecentProjects
                            projects={this.props.recentProjects}
                            searchKeyword={this.state.searchKeyword}
                            onProjectFileUpload={this.onProjectFileUpload}
                            onProjectFileUploadError={this.onProjectFileUploadError}
                            onItemClick={this.freshLoadSelectedProject}
                            onItemDelete={(project) => this.deleteConfirmRef.current.open(project)}
                        />
                        <Users
                            users={this.props.impersonation.impersonators}
                            searchKeyword={this.state.searchKeyword}
                            onProjectClick={this.freshLoadSelectedProject}
                            onProjectDelete={(project) => this.deleteConfirmRef.current.open(project)}
                        />
                    </div>
                </div>
                <Confirm title="Delete Project"
                    ref={this.deleteConfirmRef as any}
                    message={(project: IProject) => `${strings.homePage.deleteProject.confirmation} ${project.name}?`}
                    confirmButtonTheme={getPrimaryRedTheme()}
                    onConfirm={this.deleteProject} />

                <HomeProjectView
                    ref={this.homeProjectViewRef}
                    recentProjects={this.props.recentProjects}
                    connections={this.props.connections}
                    createNewProject={this.createNewProject}
                    onProjectFileUpload={this.onProjectFileUploadError}
                    onProjectFileUploadError={this.onProjectFileUploadError}
                    onOpenCloudProjectClick={this.onOpenCloudProjectClick}
                    loadSelectedProject={this.loadSelectedProject}
                    freshLoadSelectedProject={this.freshLoadSelectedProject}
                    deleteProject={this.deleteProject}
                />

                <Alert
                    show={this.state.isError}
                    title={this.state.errorTitle || "Error"}
                    message={this.state.errorMessage}
                    onClose={() => this.setState({
                        isError: false,
                        errorTitle: undefined,
                        errorMessage: undefined,
                    })}
                />
                {(this.state.projectsLoading || this.state.projectLoading) && <div className="model-loading">
                    <div className="model-loading-spinner">
                        <Spinner
                            size={SpinnerSize.large}
                            label={this.state.projectsLoading ? strings.homePage.loadingProjects : strings.homePage.loadingProject}
                        />
                    </div>
                </div>}
            </div>
        );
    }

    private createNewProject = (e: SyntheticEvent | MouseEvent<any>) => {
        this.props.projectActions.closeProject();
        this.props.history.push("/projects/create");

        e.preventDefault();
    }

    private onPrebuiltClicked = () => {
        this.props.history.push("/prebuilts-analyze");
    }

    private onUseLayoutToGetTextAndTAblesClicked = () => {
        this.props.history.push("/layout-analyze");
    }

    private onTrainAndUseAModelWithLables = () => {
        this.homeProjectViewRef.current.open();
    }

    private onOpenCloudProjectClick = () => {
        this.homeProjectViewRef.current.close();
        this.cloudFilePickerRef.current.open();
    }

    private loadSelectedProject = async (project: IProject) => {
        try {
            this.setState({ projectLoading: true })
            const loadedProject = await this.props.projectActions.loadProject(project);
            if (loadedProject !== null) {
                this.props.history.push(`/projects/${project.id}/edit`);
            }
        } catch (error) {
            this.setState({ projectLoading: false })
            if (error instanceof AppError && error.errorCode === ErrorCode.SecurityTokenNotFound) {
                toast.error(strings.errors.securityTokenNotFound.message, { autoClose: 5000 });
            }
            if (error instanceof AppError && error.errorCode === ErrorCode.ProjectInvalidSecurityToken) {
                toast.error(strings.errors.projectInvalidSecurityToken.message, { autoClose: 5000 });
            }
        }
    }

    private freshLoadSelectedProject = async (project: IProject) => {

        // Load project from storage provider to keep the project in latest state
        try {
            await this.loadSelectedProject(fillTagsColor(project));
        } catch (err) {
            if (err instanceof AppError && err.errorCode === ErrorCode.BlobContainerIONotFound) {
                const reason = interpolate(strings.errors.projectNotFound.message, { file: `${project.name}${constants.projectFileExtension}`, container: project.sourceConnection.name });
                toast.error(reason, { autoClose: false });
                return;
            }
            throw err;
        }
    }

    private deleteProject = async (project: IProject) => {
        try {
            await this.props.projectActions.deleteProject(project);
        } catch (error) {
            if (error instanceof AppError && error.errorCode === ErrorCode.SecurityTokenNotFound) {
                toast.error(error.message, { autoClose: false });
            }
            else {
                throw error;
            }
        }
    }

    private onProjectFileUpload = async (e, project) => {
        let projectJson: IProject;

        try {
            projectJson = JSON.parse(project.content);
        } catch (error) {
            throw new AppError(ErrorCode.ProjectInvalidJson, "Error parsing JSON");
        }

        if (projectJson.name === null || projectJson.name === undefined) {
            try {
                await this.importConfirmRef.current.open(project);
            } catch (e) {
                throw new Error(e.message);
            }
        } else {
            await this.loadSelectedProject(projectJson);
        }
    }

    private onProjectFileUploadError = (e, error: any) => {
        if (error instanceof AppError) {
            throw error;
        }
    }

    private onCloudPickerClick = (content) => {
        const project = JSON.parse(content);
        this.loadSelectedProject(fillTagsColor(project));
    }
}
